/**
 * _searchBoxA11y.js — Algolia search box markup adjustments
 *
 * autocomplete-js renders the magnifying glass as a submit button wrapped in
 * the input's <label>, and (in detached mode) a text "Cancel" button. There is
 * no option to change that structure, so we adjust the DOM after mount:
 * - label: icon + sr-only "Search" (just a label, no longer a button)
 * - submit: text "Search" button moved into the input suffix (after clear)
 * - detached cancel: "x" icon + sr-only "Close"
 *
 * autocomplete-js keeps references to these elements and only toggles
 * attributes on them, so moving/re-filling them is safe. Both functions are
 * idempotent — the header's detached form is re-attached on every open.
 */

const SEARCH_ICON = `<svg class="aa-SubmitIcon" aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M16.041 15.856c-0.034 0.026-0.067 0.055-0.099 0.087s-0.060 0.064-0.087 0.099c-1.258 1.213-2.969 1.958-4.855 1.958-1.933 0-3.682-0.782-4.95-2.050s-2.050-3.017-2.050-4.95 0.782-3.682 2.050-4.95 3.017-2.050 4.95-2.050 3.682 0.782 4.95 2.050 2.050 3.017 2.050 4.95c0 1.886-0.745 3.597-1.959 4.856zM21.707 20.293l-3.675-3.675c1.231-1.54 1.968-3.493 1.968-5.618 0-2.485-1.008-4.736-2.636-6.364s-3.879-2.636-6.364-2.636-4.736 1.008-6.364 2.636-2.636 3.879-2.636 6.364 1.008 4.736 2.636 6.364 3.879 2.636 6.364 2.636c2.125 0 4.078-0.737 5.618-1.968l3.675 3.675c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414z"></path></svg>`;

const CLOSE_ICON = `<svg class="aa-DetachedCancelIcon" aria-hidden="true" focusable="false" fill="none" width="16" height="16" viewBox="0 0 16 16"><path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1.5"/></svg>`;

/**
 * enhanceSearchForm()
 *
 * Turns the label into an icon-only label and moves a text submit button
 * after the input. `root` is any element containing the .aa-Form.
 */
export function enhanceSearchForm(root = document) {
  const form = root?.querySelector('.aa-Form');
  if (!form || form.dataset.mciEnhanced) return;

  const label = form.querySelector('.aa-Label');
  const submit = form.querySelector('.aa-SubmitButton');
  if (!label || !submit) return;

  form.dataset.mciEnhanced = 'true';

  // Label keeps autocomplete's id/for attributes, so the input stays labelled
  label.innerHTML = `${SEARCH_ICON}<span class="sr-only">Search</span>`;

  // Mousedown on the label would blur the input before the label click
  // refocuses it. That blur/focus cycle makes autocomplete cancel and restart
  // its requests, leaving the loading indicator showing. Keep focus where it
  // is; the label click still focuses the input when it isn't already.
  label.addEventListener('mousedown', (event) => event.preventDefault());

  submit.removeAttribute('title');
  submit.classList.add('button');
  submit.textContent = 'Search';
  // Into the suffix, after the clear button (suffix already has order: 4)
  (form.querySelector('.aa-InputWrapperSuffix') || form).append(submit);
}

/**
 * enhanceDetachedCancel()
 *
 * Swaps the detached modal's "Cancel" text for an "x" icon.
 */
export function enhanceDetachedCancel(root = document) {
  const cancel = root.querySelector('.aa-DetachedCancelButton');
  if (!cancel || cancel.dataset.mciEnhanced) return;

  cancel.dataset.mciEnhanced = 'true';
  cancel.innerHTML = `${CLOSE_ICON}<span class="sr-only">Close</span>`;
}
